/**
 * Browser-context auto-capture. Runs from page load until the user opens
 * the widget; whatever's been collected so far is attached to the request.
 */

interface CapturedContext {
  url: string;
  userAgent: string;
  viewport: { width: number; height: number };
  consoleErrors: string[];
  referrer: string | null;
}

const MAX_ERRORS = 50;
const errors: string[] = [];

function recordError(message: string): void {
  if (errors.length >= MAX_ERRORS) return;
  // Truncate to keep payloads small
  errors.push(message.length > 2000 ? message.slice(0, 2000) + '…' : message);
}

let installed = false;

/**
 * Patches console.error and listens to window.onerror / unhandledrejection
 * so we capture client-side errors that happen BEFORE the widget opens.
 *
 * The patches are idempotent and intentionally non-destructive — we still
 * call through to the original handlers so existing app behavior is preserved.
 */
export function installErrorCapture(): void {
  if (installed) return;
  installed = true;

  const origConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const msg = args.map((a) => {
        if (a instanceof Error) return `${a.name}: ${a.message}`;
        if (typeof a === 'object' && a !== null) {
          try { return JSON.stringify(a); } catch { return String(a); }
        }
        return String(a);
      }).join(' ');
      recordError(msg);
    } catch { /* never throw from console hook */ }
    origConsoleError(...args);
  };

  window.addEventListener('error', (event) => {
    const e = event.error;
    if (e instanceof Error) {
      recordError(`${e.name}: ${e.message} @ ${event.filename}:${event.lineno}:${event.colno}`);
    } else if (event.message) {
      recordError(`${event.message} @ ${event.filename}:${event.lineno}:${event.colno}`);
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (reason instanceof Error) {
      recordError(`UnhandledPromise: ${reason.name}: ${reason.message}`);
    } else {
      recordError(`UnhandledPromise: ${String(reason).slice(0, 500)}`);
    }
  });
}

export function captureContext(): CapturedContext {
  return {
    url: window.location.href,
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    consoleErrors: errors.slice(),
    referrer: document.referrer || null,
  };
}
